import { Routes } from '@angular/router';
import { Observable, of as observableOf } from 'rxjs';

import { env } from '@amfam/environment';

import { FeatureFlagGuard } from '../core';
import { ChangeRequestConfirmationComponent } from './containers/change-request-confirmation/change-request-confirmation.component';
import { PlchangerequestComponent } from './containers/plchangerequest/plchangerequest.component';
import { CoverageChangeConfirmationComponent } from './coverage-change/coverage-change-confirmation/coverage-change-confirmation.component';
import { CoverageChangeGetQuoteComponent } from './coverage-change/coverage-change-get-quote/coverage-change-get-quote.component';
import { CoverageChangeReviewQuoteComponent } from './coverage-change/coverage-change-review-quote/coverage-change-review-quote.component';
import { CoverageChangeSelectRiskComponent } from './coverage-change/coverage-change-select-risk/coverage-change-select-risk.component';
import { CoverageChangeSendQuoteConfirmationComponent } from './coverage-change/coverage-change-send-quote-confirmation/coverage-change-send-quote-confirmation.component';
import {
  EnrollGoPaperlessComponent,
  GoPaperlessComponent,
  GoPaperlessConfirmationComponent,
  GoPaperlessSignatureComponent,
  UnenrollGoPaperlessComponent
} from './go-paperless';
import {
  KnowYourDriveComponent,
  KnowYourDriveSignatureComponent,
  KydConfirmationComponent
} from './know-your-drive';
import { PartialHouseholdUnenrollConfirmationComponent } from './know-your-drive-partial-household/containers/kyd-partial-household-unenroll-confirmation/kyd-partial-household-unenroll-confirmation.component';
import { KydPartialHouseholdComponent } from './know-your-drive-partial-household/containers/kyd-partial-household/kyd-partial-household.component';
import { LifeComponent } from './life';
import { PlautoLargeComponent } from './plauto-large';
import { PlpropertyLargeComponent } from './plproperty-large';
import { PlumbrellaLargeComponent } from './plumbrella-large';
import { PolicyListComponent } from './policy-list';
import { PolicyResolve } from './policy-replaceVehicle-resolver.service';
import { OnlineBindFailureComponent } from './replace-vehicle/online-bind-failure/online-bind-failure.component';
import { ReplaceVehicleConfirmationComponent } from './replace-vehicle/replace-vehicle-confirmation/replace-vehicle-confirmation.component';
import { ReplaceVehicleGracePeriodComponent } from './replace-vehicle/replace-vehicle-grace-period/replace-vehicle-grace-period.component';
import { ReplaceVehicleReviewComponent } from './replace-vehicle/replace-vehicle-review/replace-vehicle-review.component';
import { ReplaceVehicleRiskSelectionComponent } from './replace-vehicle/replace-vehicle-risk-selection/replace-vehicle-risk-selection.component';
import { ReplaceVehicleComponent } from './replace-vehicle/replace-vehicle.component';
import { UnderwritingQuestionListComponent } from './replace-vehicle/underwriting-question-list/underwriting-question-list.component';
import { CoverageChangeGuard } from './services/coverage-change.guard';
import { ChangeRequestErrorGuard } from './services/plchangerequest-guard';
import { PolicyBreadcrumbResolver } from './services/policy-breadcrumb.resolver';
import { NewDocumentGuard } from './shared/guard/new-document-redirect.guard';
import { PolicyCustomerGuard } from './shared/guard/policy.guard';
import { ReplaceVehicleEmailGuard } from './shared/guard/replace-vehicle-email.guard';
import { ReplaceVehicleLookInGuard } from './shared/guard/replace-vehicle.guard';

export function param(params: any): Observable<string> {
  // auto, prop, and umbrella just want the param as breadcrumb.
  return observableOf(params.id);
}

export const PolicyRoutes: Routes = [
  {
    path: 'policies',
    data: {
      breadcrumb: 'Policies'
    },
    canActivate: [PolicyCustomerGuard],
    children: [
      { path: '', component: PolicyListComponent, data: { breadcrumb: false } },
      {
        path: 'webview',
        children: [
          {
            path: 'auto/:id',
            component: PlautoLargeComponent,
            resolve: { resolvedData: PolicyBreadcrumbResolver },
            data: { breadcrumb: 'useResolvedData', resolveKey: 'auto' }
          },
          {
            path: 'property/:id',
            component: PlpropertyLargeComponent,
            resolve: { resolvedData: PolicyBreadcrumbResolver },
            data: { breadcrumb: 'useResolvedData', resolveKey: 'property' }
          },
          {
            path: 'umbrella/:id',
            component: PlumbrellaLargeComponent,
            resolve: { resolvedData: PolicyBreadcrumbResolver },
            data: { breadcrumb: 'useResolvedData', resolveKey: 'umbrella' }
          },
          {
            path: 'life/:id',
            component: LifeComponent,
            resolve: { resolvedData: PolicyBreadcrumbResolver },
            data: { breadcrumb: 'useResolvedData', resolveKey: 'life' }
          }
        ]
      },
      {
        path: 'auto/:id',
        component: PlautoLargeComponent,
        resolve: { resolvedData: PolicyBreadcrumbResolver },
        data: { breadcrumb: 'useResolvedData', resolveKey: 'auto' }
      },
      {
        path: 'property/:id',
        component: PlpropertyLargeComponent,
        resolve: { resolvedData: PolicyBreadcrumbResolver },
        data: { breadcrumb: 'useResolvedData', resolveKey: 'property' }
      },
      {
        path: 'umbrella/:id',
        component: PlumbrellaLargeComponent,
        resolve: { resolvedData: PolicyBreadcrumbResolver },
        data: { breadcrumb: 'useResolvedData', resolveKey: 'umbrella' }
      },
      {
        path: 'life/:id',
        component: LifeComponent,
        resolve: { resolvedData: PolicyBreadcrumbResolver },
        data: { breadcrumb: 'useResolvedData', resolveKey: 'life' }
      },
      {
        path: 'changerequest',
        component: PlchangerequestComponent,
        canDeactivate: [ChangeRequestErrorGuard],
        data: { breadcrumb: 'Request a Change' }
      },
      {
        path: 'changerequest/confirmation',
        component: ChangeRequestConfirmationComponent,
        data: { breadcrumb: 'Request a Change' }
      },
      {
        path: 'changerequest/:id',
        component: PlchangerequestComponent,
        data: { breadcrumb: 'Request a Change' }
      },
      { path: 'gopaperless', component: GoPaperlessComponent },
      {
        path: 'risk-selection',
        data: {
          breadcrumb: 'Add/Replace Vehicle'
        },
        children: [
          // TODO - need selected policy number for breadcrumbs -- see claims fnol routing for example
          {
            path: '',
            component: ReplaceVehicleRiskSelectionComponent,
            data: { breadcrumb: false }
          },
          {
            path: 'multiple-policies',
            component: ReplaceVehicleGracePeriodComponent,
            data: { breadcrumb: false, multiplePolicies: true }
          },
          {
            path: 'grace-period',
            component: ReplaceVehicleGracePeriodComponent,
            data: { breadcrumb: false }
          },
          {
            path: 'confirmation',
            component: ReplaceVehicleConfirmationComponent,
            data: { breadcrumb: false }
          },
          {
            path: 'replace-vehicle-quick-quote',
            canActivate: [FeatureFlagGuard],
            data: {
              breadcrumb: 'lazy_loaded_module',
              feature: 'replace_vehicle_quickquote'
            },
            loadChildren: () =>
              import('@amfam/policy/replace-vehicle/feature').then(
                m => m.PolicyReplaceVehicleFeatureModule
              )
          }
        ]
      },
      {
        path: 'add-vehicle',
        canActivate: [FeatureFlagGuard],
        data: {
          breadcrumb: 'Add Vehicle',
          feature: 'add_vehicle'
        },
        loadChildren: () =>
          import('@amfam/policy/add-vehicle/feature').then(m => {
            m.PolicyAddVehicleFeatureModule.environment = env;
            return m.PolicyAddVehicleFeatureModule;
          })
      },
      {
        path: 'change-coverage',

        data: {
          breadcrumb: 'Edit Coverage Amounts',
          feature: 'partner_coverage_change'
        },
        loadChildren: () =>
          import('@amfam/policy/change-coverage/feature').then(
            m => m.PolicyChangeCoverageFeatureModule
          )
      },
      {
        path: 'file-upload/:id',
        canActivate: [FeatureFlagGuard],
        data: {
          breadcrumb: 'lazy_loaded_module',
          feature: 'file-upload'
        },
        loadChildren: () =>
          import('@amfam/policy/file-upload/feature').then(m => m.FileUploadRoutes)
      },
      {
        path: 'replace-vehicle',
        data: {
          breadcrumb: 'Replace a Vehicle'
        },
        children: [
          // TODO - need selected policy number for breadcrumbs -- see claims fnol routing for example
          {
            path: '',
            component: ReplaceVehicleRiskSelectionComponent,
            data: { breadcrumb: false }
          },

          {
            path: ':id',
            component: ReplaceVehicleComponent,
            canActivate: [ReplaceVehicleLookInGuard],
            canDeactivate: [ReplaceVehicleLookInGuard],
            resolve: {
              resolvedData: PolicyResolve
            },
            data: {
              breadcrumb: 'useResolvedData'
            }
          },
          {
            path: ':id/underwriting',
            component: UnderwritingQuestionListComponent,
            canActivate: [ReplaceVehicleLookInGuard],
            canDeactivate: [ReplaceVehicleLookInGuard],
            resolve: {
              resolvedData: PolicyResolve
            },
            data: {
              breadcrumb: 'useResolvedData'
            }
          },
          {
            path: ':id/offlinebind',
            component: OnlineBindFailureComponent,
            canActivate: [ReplaceVehicleLookInGuard],
            canDeactivate: [ReplaceVehicleLookInGuard],
            resolve: {
              resolvedData: PolicyResolve
            },
            data: {
              breadcrumb: 'useResolvedData'
            }
          },
          {
            path: ':id/grace-period',
            component: ReplaceVehicleGracePeriodComponent,
            canActivate: [ReplaceVehicleLookInGuard],
            canDeactivate: [ReplaceVehicleLookInGuard],
            data: { breadcrumb: false }
          },
          {
            path: ':id/review',
            component: ReplaceVehicleReviewComponent,
            canActivate: [ReplaceVehicleLookInGuard],
            canDeactivate: [ReplaceVehicleLookInGuard, ReplaceVehicleEmailGuard],
            data: { breadcrumb: false }
          },
          {
            path: ':id/confirmation',
            component: ReplaceVehicleConfirmationComponent,
            canActivate: [ReplaceVehicleLookInGuard],
            canDeactivate: [ReplaceVehicleLookInGuard],
            data: { breadcrumb: false }
          }
        ]
      },
      {
        path: 'gopaperless/enroll',
        component: EnrollGoPaperlessComponent,
        data: {
          breadcrumb: 'Sign Up for Paperless Policy Documents'
        }
      },
      {
        path: 'gopaperless/unenroll',
        component: UnenrollGoPaperlessComponent,
        data: {
          breadcrumb: 'Opt Out of Paperless Policy Documents'
        }
      },
      {
        path: 'gopaperless/signature',
        component: GoPaperlessSignatureComponent,
        data: {
          breadcrumb: 'Paperless Policy Documents Signature'
        }
      },
      {
        path: 'gopaperless/confirmation',
        component: GoPaperlessConfirmationComponent,
        data: {
          breadcrumb: 'Confirmation'
        }
      },
      {
        path: 'knowyourdrive',
        component: KnowYourDriveComponent,
        data: {
          breadcrumb: 'Enroll in KnowYourDrive'
        }
      },
      {
        path: 'knowyourdrive/signature',
        component: KnowYourDriveSignatureComponent,
        data: {
          breadcrumb: 'KnowYourDrive Signature'
        }
      },
      {
        path: 'knowyourdrive/confirmation',
        component: KydConfirmationComponent,
        data: {
          breadcrumb: 'KnowYourDrive Confirmation'
        }
      },
      {
        path: 'knowyourdrivesmartphone',
        component: KydPartialHouseholdComponent,
        data: {
          breadcrumb: 'Enroll in KnowYourDrive Smartphone Program'
        }
      },
      {
        path: 'knowyourdrivesmartphone/confirmation',
        component: PartialHouseholdUnenrollConfirmationComponent,
        data: {
          breadcrumb: 'Confirmation'
        }
      },
      {
        path: 'coverage-change',
        data: {
          breadcrumb: 'none'
        },
        children: [
          {
            path: '',
            component: CoverageChangeSelectRiskComponent
          },
          {
            path: 'sent-quote-confirmation',
            canActivate: [CoverageChangeGuard],
            component: CoverageChangeSendQuoteConfirmationComponent
          },
          {
            path: 'change-confirmation',
            canActivate: [CoverageChangeGuard],
            component: CoverageChangeConfirmationComponent
          },
          {
            path: ':id',
            component: CoverageChangeSelectRiskComponent
          },
          {
            path: ':id/get-quote',
            component: CoverageChangeGetQuoteComponent
          },
          {
            path: ':id/review',
            canActivate: [CoverageChangeGuard],
            component: CoverageChangeReviewQuoteComponent
          },
          {
            path: ':id/send-quote',
            component: CoverageChangeSendQuoteConfirmationComponent
          }
        ]
      }
    ]
  },
  {
    path: 'newdocumentlink/:id',
    canActivate: [NewDocumentGuard],
    component: PolicyListComponent
  }
];

export const PolicyGuards = [
  PolicyCustomerGuard,
  ReplaceVehicleLookInGuard,
  ReplaceVehicleEmailGuard,
  ChangeRequestErrorGuard,
  NewDocumentGuard
];
